import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { IonContent, NavController, Platform } from "@ionic/angular";
import { Navigator } from "src/app/components/menu/navigator";
import { SuccessMessageService } from "src/app/services/message-provider/success-message.service";
import { CustomerModel } from "src/app/models/customer-model";
import { AuthenticationService } from "src/app/services/authentication/authentication.service";
import { ConfigService } from "src/app/services/config/config.service";
import {
  noWhitespaceOnlyValidator,
  PASSWORD_PATTERN,
} from "src/app/services/customFormValidators";
import { EventsService } from "src/app/services/event/event.service";
import { UserSessionService } from "src/app/services/user-session/user-session.service";

@Component({
  selector: "app-change-password",
  templateUrl: "./change-password.component.html",
  styleUrls: ["./change-password.component.scss"],
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  @ViewChild(IonContent, { static: false }) content: IonContent;

  public error: any = null;
  public passwordForm: FormGroup;

  public customer: CustomerModel;

  public currentPassword: AbstractControl;
  public passwordGroup: AbstractControl;
  public newPassword: AbstractControl;
  public confirmPassword: AbstractControl;
  public securityGroup: AbstractControl;
  public securityAnswer: AbstractControl;
  public secondSecurityAnswer: AbstractControl;
  public playerForceResetCheck: boolean = false;
  public customerWillRedirect: boolean = false;
  
  public securityQuestionsRequiredMFA: boolean = false;
  private cleanup: any;
  @Input() playerMigrationCheck: boolean;
  @Input() callBackFn: () => void;

  constructor(
    // public logger: Logger,
    public navCtrl: NavController,
    public authService: AuthenticationService,
    public userSession: UserSessionService,
    public successMessageService: SuccessMessageService,
    protected config: ConfigService,
    public events: EventsService,
    public navigator: Navigator,
    public platform: Platform
  ) {
    this.successMessageService.clearMessage();

    this.userSession.getCustomer().subscribe((customer) => {
      if (customer) {
        this.customer = customer;
      }
    });
  }

  ngOnInit() {
    //setting up playerForceResetCheck flag
    if (this.playerMigrationCheck) {
      this.playerForceResetCheck = true;
    }
    this.securityQuestionsRequiredMFA = this.config.getConfig().FEATURE_SECURITY_QUESTIONS_REQUIRED == "1" || this.config.getConfig().FEATURE_SECURITY_QUESTIONS_REQUIRED == null ? true : false;

    this.passwordForm = new FormGroup({
      currentPassword: new FormControl("", [Validators.required]),
      passwordGroup: new FormGroup(
        {
          newPassword: new FormControl("", [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(PASSWORD_PATTERN),
          ]),
          confirmPassword: new FormControl("", [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(PASSWORD_PATTERN),
          ]),
        },
        this.passwordMatchValidator
      ),
      securityGroup: this.securityQuestionsRequiredMFA ? new FormGroup({
        securityAnswer: new FormControl("", [
          Validators.required,
          noWhitespaceOnlyValidator,
        ]),
        secondSecurityAnswer: new FormControl("", [
          Validators.required,
          noWhitespaceOnlyValidator,
        ]),
      }) : new FormGroup({}),
    });

    this.currentPassword = this.passwordForm.get("currentPassword");
    this.passwordGroup = this.passwordForm.get("passwordGroup");
    this.newPassword = this.passwordGroup.get("newPassword");
    this.confirmPassword = this.passwordGroup.get("confirmPassword");

    if (!this.playerForceResetCheck && this.securityQuestionsRequiredMFA) {
      this.securityGroup = this.passwordForm.get("securityGroup");
      this.securityAnswer = this.securityGroup.get("securityAnswer");
      this.secondSecurityAnswer = this.securityGroup.get('secondSecurityAnswer');
    }

    if (this.platform.is('mobile')) {
      this.cleanup = this.checkForKeyboardOpen();
    }
  }

  ngOnDestroy() {
    if (this.cleanup) { this.cleanup(); }
  }

  passwordMatchValidator(g: FormGroup) {
    return g.get("newPassword").value === g.get("confirmPassword").value
      ? null
      : { mismatch: true };
  }

  ionViewDidLeave() {
    if (this.customerWillRedirect == true) {
      this.events.publish("event:appInit:next");
    }
  }

  onSubmit() {
    this.error = null;
    this.successMessageService.clearMessage();

    //Seprating out playMigration call for changePassword
    if (this.playerForceResetCheck) {
      this.authService
        .callForceChangePassword(this.passwordForm.value)
        .subscribe(
          (response: any) => {
            if (response && !!response.result) {
              this.error = null;
              this.successMessageService.setMessage(
                "Your password has been changed."
              );

              this.content.scrollToTop(10);
              this.customerWillRedirect = true;
              if (this.callBackFn) {
                this.callBackFn();
              } else {
                this.events.publish("event:appInit:next");
              }
            }
          },
          (error) => {
            console.error("callForceChangePassword ", error);

            switch (error.getErrorCode().toUpperCase()) {
              case "CURRENT_PASS_FAIL":
                error.setErrorMessage(
                  "You have incorrectly entered your current password."
                );
                this.currentPassword.setErrors({ CURRENT_PASS_FAIL: true });
                this.currentPassword.markAsDirty();
                this.currentPassword.markAsTouched();
                break;
              case "CURRENT_PASS_NO_MATCH":
                error.setErrorMessage(
                  "Your new password can not match your current password."
                );
                this.passwordGroup.setErrors({ CURRENT_PASS_NO_MATCH: true });
                this.passwordGroup.markAsDirty();
                this.passwordGroup.markAsTouched();
                break;
              case "SECURE_NO_MATCH":
                error.setErrorMessage(
                  "You have incorrectly answered your security questions."
                );
                this.securityGroup.setErrors({ SECURE_NO_MATCH: true });
                this.securityGroup.markAsDirty();
                this.securityGroup.markAsTouched();
                break;
            }
            this.error = error;
            this.content.scrollToTop(10);
          }
        );
    } else if(!this.securityQuestionsRequiredMFA) {
      //no sec questions
      this.authService.callChangePasswordNoSec(this.passwordForm.value, this.customer.email).subscribe(
        (response: any) => {
          if (response && !!response.result) {
            this.error = null;
            this.successMessageService.setMessage(
              "Your password has been changed."
            );
  
            this.content.scrollToTop();
          }
        },
        (error) => {
  
          switch (error.getErrorCode().toUpperCase()) {
            case "CURRENT_PASS_FAIL":
              error.setErrorMessage(
                "You have incorrectly entered your current password."
              );
              this.currentPassword.setErrors({ CURRENT_PASS_FAIL: true });
              this.currentPassword.markAsDirty();
              this.currentPassword.markAsTouched();
              break;
            case "CURRENT_PASS_NO_MATCH":
              error.setErrorMessage(
                "Your new password can not match your current password."
              );
              this.passwordGroup.setErrors({ CURRENT_PASS_NO_MATCH: true });
              this.passwordGroup.markAsDirty();
              this.passwordGroup.markAsTouched();
              break;
              break;
          }
          this.error = error;
          this.content.scrollToTop(10);
        }
      );
    } 
    else {
      //Noraml ChangePassword Call
      this.authService.callChangePassword(this.passwordForm.value).subscribe(
        (response: any) => {
          if (response && !!response.result) {
            this.error = null;
            this.successMessageService.setMessage(
              "Your password has been changed."
            );

            this.content.scrollToTop();
          }
        },
        (error) => {

          switch (error.getErrorCode().toUpperCase()) {
            case "CURRENT_PASS_FAIL":
              error.setErrorMessage(
                "You have incorrectly entered your current password."
              );
              this.currentPassword.setErrors({ CURRENT_PASS_FAIL: true });
              this.currentPassword.markAsDirty();
              this.currentPassword.markAsTouched();
              break;
            case "CURRENT_PASS_NO_MATCH":
              error.setErrorMessage(
                "Your new password can not match your current password."
              );
              this.passwordGroup.setErrors({ CURRENT_PASS_NO_MATCH: true });
              this.passwordGroup.markAsDirty();
              this.passwordGroup.markAsTouched();
              break;
            case "SECURE_NO_MATCH":
              error.setErrorMessage(
                "You have incorrectly answered your security questions."
              );
              this.securityGroup.setErrors({ SECURE_NO_MATCH: true });
              this.securityAnswer.setErrors({ SECURE_NO_MATCH: true });
              this.secondSecurityAnswer.setErrors({ SECURE_NO_MATCH: true });
              break;
          }
          this.error = error;
          this.content.scrollToTop(10);
        }
      );
    }
  }

  closeModal = () => {
    if (this.customerWillRedirect) {
      if (this.callBackFn) {
        this.callBackFn();
      } else {
        this.events.publish("event:appInit:next");
      }
    } else {
      if (this.playerForceResetCheck) {
        this.navigator.navigateTo(
          "LogoutPage",
          { forceModal: true },
          { reason: "INCOMPLETE_FORCE_RESET_PASSWORD" }
        );
      } else {
        // this.navigator.toLobby(); //NEEDS to IMPLEMENT THIS LATER
      }
    }
  };
  
  protected checkForKeyboardOpen(): () => void {
    const element = document.getElementById('content-container') as HTMLElement | null;
    const onFocus = (): void => { this.applyPadding(element, true); };
    const onBlur = (): void => { this.applyPadding(element, false);};
    document.addEventListener('focus', onFocus, true);
    document.addEventListener('blur', onBlur, true);
    return () => {
        document.removeEventListener('focus', onFocus, true);
        document.removeEventListener('blur', onBlur, true);
    };
  }
  protected applyPadding(element: HTMLElement | null, isOpen: boolean): void {
      if (element) {
          element.style.paddingBottom = isOpen ? '130px' : '0';
      }
  }
}
